"use client"

import React, { createContext, useContext, useEffect, useState } from "react"
import { useReCaptcha } from "next-recaptcha-v3"

import { SYSTEM_BE } from "@/lib/constants"

interface Message {
    id: number
    content: string
    sender: "user" | "bot"
}

type ContextType = {
    messages: Message[]
    inputValue: string
    setInputValue: React.Dispatch<React.SetStateAction<string>>
    sendMessage: (branchId: string) => void
    loading: boolean
    open: boolean
    setOpen: React.Dispatch<React.SetStateAction<boolean>>
    unReadCount: number
    setUnReadCount: React.Dispatch<React.SetStateAction<number>>
}

const Context = createContext<null | ContextType>(null)

export const ChatbotProvider = ({ children }: { children: React.ReactNode }) => {

    const { executeRecaptcha } = useReCaptcha()
    const [loading, setLoading] = useState<boolean>(false)
    const [open, setOpen] = useState<boolean>(false)
    const [unReadCount, setUnReadCount] = useState(1)
    const [messages, setMessages] = useState<Message[]>([
        { id: 1, content: "¡Hola! si necesitas ayuda con una reservación o si quieres conocer más información estoy aqui para ayudarte.", sender: "bot" },
    ])
    const [inputValue, setInputValue] = useState("")

    const playNotificationSound = () => {
        const audio = new Audio("/sound/notification.mp3")
        audio.play().catch(error => console.error("Error al reproducir el sonido:", error))
    }

    const sendMessage = async (branchId: string) => {
        if (!inputValue.trim()) return

        const newMessage: Message = {
            id: messages.length + 1,
            content: inputValue,
            sender: "user",
        }

        setMessages([...messages, newMessage])
        setInputValue("")
        setLoading(true)

        const recaptchaToken = await executeRecaptcha("chatbot")

        const recaptchaFormData = new FormData()
        recaptchaFormData.append("recaptcha-token", recaptchaToken)

        const recaptchaResponse = await fetch("/api/recaptcha", {
            method: "POST",
            body: recaptchaFormData
        })

        if (!recaptchaResponse.ok) {
            const botResponse: Message = {
                id: messages.length + 2,
                content: "El servicio no esta disponible en estos momentos, intenta mas tarde",
                sender: "bot",
            }
            setMessages((prevMessages) => [...prevMessages, botResponse])
            setUnReadCount(prev => prev + 1)
            playNotificationSound()
                setLoading(false)
            return
        }

        fetch(SYSTEM_BE + "/v1/branch/info", {
            method: "POST",
            body: JSON.stringify({
                "question": newMessage.content,
                "branch": branchId
            }),
            headers: {
                "Content-Type": "application/json"
            }
        })
            .then(res => {
                if (!res.ok) {
                    throw new Error("No se pudo enviar el mensaje")
                }
                return res.text()
            })
            .then(data => {
                if (data) {
                    const botResponse: Message = {
                        id: messages.length + 2,
                        content: data,
                        sender: "bot",
                    }
                    setMessages((prevMessages) => [...prevMessages, botResponse])
                    setUnReadCount(prev => prev + 1)
                    playNotificationSound()
                }
            })
            .catch(() => {
                const botResponse: Message = {
                    id: messages.length + 2,
                    content: "Ha ocurrido un error inesperado, por favor intenta más tarde.",
                    sender: "bot",
                }
                setMessages((prevMessages) => [...prevMessages, botResponse])
                setUnReadCount(prev => prev + 1)
                playNotificationSound()
            })
            .finally(() => {
                setLoading(false)
            })
    }

    useEffect(() => {
        if (unReadCount === 0) return
        if (messages.length > 1) return
        if (localStorage.getItem('chat-notification-sound-played')) return

        localStorage.setItem('chat-notification-sound-played', 'true')
        const timer = setTimeout(() => {
            if (unReadCount > 0 && messages.length === 1) {
                playNotificationSound()
            }
        }, 15000)

        return () => clearTimeout(timer)
    }, [unReadCount, messages.length])

    return <Context.Provider value={{ messages, inputValue, setInputValue, sendMessage, loading, open, setOpen, unReadCount, setUnReadCount }}>
        {children}
    </Context.Provider>
}


export function useChatbot() {
    const context = useContext(Context)
    if (!context) {
        throw new Error('useChatbot should be used inside a ChatbotProvider')
    }
    return context
}
